import { Component } from '@theme/component';

const FOCUSABLE_SELECTOR = ['a[href]', 'button', 'input', 'select', 'textarea', 'summary', '[tabindex]'].join(',');

/**
 * Keeps focusable descendants out of the tab order while a slide is hidden.
 * @param {HTMLElement} slide
 * @param {boolean} hidden
 */
function setSlideHidden(slide, hidden) {
  slide.setAttribute('aria-hidden', `${hidden}`);
  slide.inert = hidden;

  slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLElement)) return;

    if (hidden) {
      if (!element.hasAttribute('data-slide-hidden-tabindex')) {
        element.setAttribute('data-slide-hidden-tabindex', element.getAttribute('tabindex') ?? '');
      }
      element.setAttribute('tabindex', '-1');
      return;
    }

    if (!element.hasAttribute('data-slide-hidden-tabindex')) return;

    const previousTabindex = element.getAttribute('data-slide-hidden-tabindex');
    if (previousTabindex) {
      element.setAttribute('tabindex', previousTabindex);
    } else {
      element.removeAttribute('tabindex');
    }
    element.removeAttribute('data-slide-hidden-tabindex');
  });
}

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.current = this.#current;
    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      setSlideHidden(slide, index !== relativeIndex);
    });
  }

  /**
   * Suspend/resume the slideshow when the page visibility changes.
   * Uses suspend/play instead of pause/resume so that the paused
   * state is not permanently set by a visibility change.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else if (!this.paused) {
      this.suspend();
      this.play();
    }
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
